// A picture's look, as live's Parameters set it (#241): its Transform, Flip, Opacity, Radius,
// Stroke, Shadow and Chroma Key, and the ranges `looked` holds each to. A text's Stroke and
// Shadow are the same (text-style.ts).

/**
 * A picture's place on the frame in live's pixels (live's frame, `LIVE_FRAME`): its top
 * left corner before it turns, and its size.
 */
export interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Live's Stroke: a line around the picture, `width` live's pixels, in `color` (`#RRGGBB`). */
export interface Stroke {
  color: string;
  width: number;
}

/** Live's Shadow: the picture's shape in `color`, moved and blurred (live's pixels), behind it. */
export interface Shadow {
  color: string;
  offsetX: number;
  offsetY: number;
  blur: number;
}

/** Live's Chroma Key: what is near `color` turns clear; similarity and spill 0 to 1. */
export interface ChromaKey {
  color: string;
  similarity: number;
  spill: number;
}

/**
 * A picture's look, as live's Parameters set it (#241): its Transform (`box`, null for the whole
 * picture fitted and centered; `rotation` in degrees, clockwise about its centre), its Flip, its
 * Opacity (0 to 1), its corners' Radius (live's pixels), and its Stroke, Shadow and Chroma Key
 * (null until added).
 */
export interface Look {
  box: Box | null;
  rotation: number;
  flipX: boolean;
  flipY: boolean;
  opacity: number;
  radius: number;
  stroke: Stroke | null;
  shadow: Shadow | null;
  chromaKey: ChromaKey | null;
}

/** A picture as it starts: fitted, upright, unflipped, opaque, square, with no extras. */
export const PLAIN_LOOK: Look = {
  box: null,
  rotation: 0,
  flipX: false,
  flipY: false,
  opacity: 1,
  radius: 0,
  stroke: null,
  shadow: null,
  chromaKey: null,
};

/** What live's "+" adds (SPEC B3): a black line 0 wide, a black shadow in place, a green key. */
export const ADDED_STROKE: Stroke = { color: "#000000", width: 0 }; // raw-color: live's default
export const ADDED_SHADOW: Shadow = { color: "#000000", offsetX: 0, offsetY: 0, blur: 0 }; // raw-color: live's default
export const ADDED_CHROMA_KEY: ChromaKey = { color: "#00FF00", similarity: 0.1, spill: 0.05 }; // raw-color: live's default
/**
 * The key Remove background sets (spec remove-background.md, decision 1): Bria's Green, measured on
 * dev as RGB (1, 128, 1), CSS green; its exact colour for ffmpeg's key, which measures the distance
 * to it. The engine clears what leans green by the similarity: Bria's flat green leans 0.5, and
 * live's 0.1 also cleared BugMD's green pouches (they lean about 0.12), which 0.4 keeps.
 */
export const BACKGROUND_KEY: ChromaKey = { color: "#008000", similarity: 0.4, spill: 0.05 }; // raw-color: Bria's Green, measured

/** Live's colours are six hex digits; anything else keeps the last good one. */
const HEX = /^#[0-9A-Fa-f]{6}$/;

/** `next` if it is a colour live takes, else `current`. */
export function colourOr(next: string, current: string): string {
  return HEX.test(next) ? next : current;
}

/** The value held between `low` and `high`. */
export function clamp(value: number, low: number, high: number): number {
  return Math.min(Math.max(value, low), high);
}

/** Live's ranges: the Radius slider, and how far a box may sit or reach. */
export const MAX_RADIUS = 500;
export const MAX_PLACE = 10_000;

/** A finite number, or the fallback. */
export function finite(value: number | undefined, fallback: number): number {
  return value !== undefined && Number.isFinite(value) ? value : fallback;
}

/**
 * A Stroke or a Shadow patch kept in live's ranges: undefined keeps the current one, null takes
 * it away, a colour that is not one keeps the last good one (live's "+" black).
 */
export function keptStroke(next: Stroke | null | undefined, was: Stroke | null): Stroke | null {
  if (next === undefined) return was;
  if (next === null) return null;
  return {
    color: colourOr(next.color, was?.color ?? ADDED_STROKE.color),
    width: clamp(finite(next.width, 0), 0, 100),
  };
}

export function keptShadow(next: Shadow | null | undefined, was: Shadow | null): Shadow | null {
  if (next === undefined) return was;
  if (next === null) return null;
  return {
    color: colourOr(next.color, was?.color ?? ADDED_SHADOW.color),
    offsetX: clamp(finite(next.offsetX, 0), -500, 500),
    offsetY: clamp(finite(next.offsetY, 0), -500, 500),
    blur: clamp(finite(next.blur, 0), 0, 100),
  };
}

/** A picture's look with the patch's parts kept in live's ranges. */
export function looked(clip: Look, patch: Partial<Look>): Look {
  const place = (value: number, low = -MAX_PLACE) => clamp(Math.round(value * 10) / 10, low, MAX_PLACE);
  const box =
    patch.box === undefined
      ? clip.box
      : patch.box === null
        ? null
        : {
            x: place(patch.box.x),
            y: place(patch.box.y),
            width: place(Math.max(1, patch.box.width), 1),
            height: place(Math.max(1, patch.box.height), 1),
          };
  const extra = <T>(next: T | null | undefined, current: T | null, keep: (one: T, was: T | null) => T) =>
    next === undefined ? current : next === null ? null : keep(next, current);
  return {
    box,
    rotation: clamp(Math.round(finite(patch.rotation, clip.rotation) * 10) / 10, -360, 360),
    flipX: patch.flipX ?? clip.flipX,
    flipY: patch.flipY ?? clip.flipY,
    opacity: clamp(finite(patch.opacity, clip.opacity), 0, 1),
    radius: clamp(Math.round(finite(patch.radius, clip.radius)), 0, MAX_RADIUS),
    stroke: keptStroke(patch.stroke, clip.stroke),
    shadow: keptShadow(patch.shadow, clip.shadow),
    chromaKey: extra(patch.chromaKey, clip.chromaKey, (next, was) => ({
      color: colourOr(next.color, was?.color ?? ADDED_CHROMA_KEY.color),
      similarity: clamp(finite(next.similarity, 0), 0, 1),
      spill: clamp(finite(next.spill, 0), 0, 1),
    })),
  };
}
